import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const ChatContext = createContext();

export function ChatProvider({ children, user }) {
  const [socket, setSocket] = useState(null);
  const [matchStatus, setMatchStatus] = useState('idle'); // idle, searching, matched
  const [onlineCount, setOnlineCount] = useState(0);
  const [partner, setPartner] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  const [partnerIsVip, setPartnerIsVip] = useState(false);
  const [partnerCustomNameColor, setPartnerCustomNameColor] = useState(null);
  const [partnerAvatarUrl, setPartnerAvatarUrl] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [roomId, setRoomId] = useState(null);
  const [subscriptionRequiredData, setSubscriptionRequiredData] = useState(null);
  const [areFriends, setAreFriends] = useState(false);

  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user) {
        if (socket) {
            socket.disconnect();
            setSocket(null);
        }
        return;
    }

    if (socket) return; // Already connected

    const socketUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3000'
      : '/';

    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      auth: { userId: user.id || user.telegramId }
    });

    const handleConnect = () => {
      newSocket.emit('authenticate', user.id || user.telegramId);
    };

    newSocket.on('connect', handleConnect);
    if (newSocket.connected) {
      handleConnect();
    }

    setSocket(newSocket);

    const handleOnlineCount = (count) => setOnlineCount(count);

    const handleMatchFound = (data) => {
      setRoomId(data.roomId);
      setPartner(data.partner);
      setPartnerId(data.partnerId);
      setPartnerIsVip(data.partnerIsVip);
      setPartnerCustomNameColor(data.partnerCustomNameColor);
      setPartnerAvatarUrl(data.partnerAvatarUrl);
      setAreFriends(data.areFriends || false);
      setMatchStatus('matched');
      setMessages([]);
      setSubscriptionRequiredData(null);
    };

    const handlePartnerProfileReveal = (data) => {
      setPartner(data.partner);
      setPartnerIsVip(data.partnerIsVip);
      setPartnerCustomNameColor(data.partnerCustomNameColor);
      setPartnerAvatarUrl(data.partnerAvatarUrl);
      setAreFriends(true);
    };

    const handleReceiveMessage = (message) => {
      setMessages((prev) => [...prev, { ...message }]);
      setTyping(false);
 
      newSocket.emit('message_delivered', { id: message.id });
      setTimeout(() => {
          newSocket.emit('message_read', { id: message.id });
      }, 1000);
    };

    const handleTyping = () => {
      setTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setTyping(false), 2000);
    };

    const handlePartnerSkipped = () => {
      setMatchStatus('searching');
      setRoomId(null);
      setPartner(null);
      setPartnerIsVip(false);
      setPartnerCustomNameColor(null);
      setPartnerAvatarUrl(null);
      setAreFriends(false);
      setMessages([]);
      newSocket.emit('join_queue', {
        id: user?.id || user?.telegramId,
        username: user?.username || user?.firstName || 'مجهول'
      });
    };

    const handlePartnerDisconnected = () => {
      setMatchStatus('idle');
      setRoomId(null);
      setPartner(null);
      setPartnerIsVip(false);
      setPartnerCustomNameColor(null);
      setPartnerAvatarUrl(null);
      setAreFriends(false);
      setMessages([]);
    };

    const handleMessageStatus = ({ id, status }) => {
        setMessages(prev => prev.map(msg => msg.id === id ? { ...msg, status } : msg));
    };

    const handleSubscriptionRequired = (data) => {
        setSubscriptionRequiredData(data);
        setMatchStatus('idle');
    };

    newSocket.on('online_count', handleOnlineCount);
    newSocket.on('match_found', handleMatchFound);
    newSocket.on('partner_profile_reveal', handlePartnerProfileReveal);
    newSocket.on('receive_message', handleReceiveMessage);
    newSocket.on('typing', handleTyping);
    newSocket.on('partner_skipped', handlePartnerSkipped);
    newSocket.on('partner_disconnected', handlePartnerDisconnected);
    newSocket.on('message_status', handleMessageStatus);
    newSocket.on('subscription_required', handleSubscriptionRequired);

    // Track usage
    const pingInterval = setInterval(() => {
        newSocket.emit('user_ping_active');
    }, 60000); // every minute

    return () => {
      clearInterval(pingInterval);
      // Logic for cleanup if needed, but since it's global provider, we might want to keep it alive
    };
  }, [user, socket]);

  const findMatch = useCallback(() => {
    if (!socket) return;
    setMatchStatus('searching');
    socket.emit('join_queue', {
        id: user?.id || user?.telegramId,
        username: user?.username || user?.firstName || 'مجهول'
    });
  }, [socket, user]);

  const skipMatch = useCallback(() => {
    if (!socket) return;
    setMatchStatus('searching');
    setMessages([]);
    setPartner(null);
    setPartnerIsVip(false);
    setPartnerCustomNameColor(null);
    setPartnerAvatarUrl(null);
    setRoomId(null);
    socket.emit('skip_match');
    socket.emit('join_queue', {
        id: user?.id || user?.telegramId,
        username: user?.username || user?.firstName || 'مجهول'
    });
  }, [socket, user]);

  const skipAndBlockMatch = useCallback(() => {
    if (!socket) return;
    setMatchStatus('searching');
    setMessages([]);
    setPartner(null);
    setPartnerIsVip(false);
    setPartnerCustomNameColor(null);
    setPartnerAvatarUrl(null);
    setRoomId(null);
    socket.emit('skip_and_block_match');
    socket.emit('join_queue', {
        id: user?.id || user?.telegramId,
        username: user?.username || user?.firstName || 'مجهول'
    });
  }, [socket, user]);

  const stopSearch = useCallback(() => {
    if (!socket) return;
    setMatchStatus('idle');
    socket.emit('skip_match');
  }, [socket]);

  const sendMessage = useCallback((text, replyTo = null) => {
    if (text.trim() && socket) {
      const newMessage = { id: Date.now(), text, sender: 'me', status: 'sending', replyTo };
      setMessages((prev) => [...prev, newMessage]);
      socket.emit('send_message', { id: newMessage.id, text, replyTo });
    }
  }, [socket]);

  const sendMediaMessage = useCallback((url, type, replyTo = null) => {
    if (socket) {
      const newMessage = { id: Date.now(), text: url, mediaType: type, sender: 'me', status: 'sending', replyTo };
      setMessages((prev) => [...prev, newMessage]);
      socket.emit('send_message', { id: newMessage.id, text: url, type, replyTo });
    }
  }, [socket]);

  const sendTyping = useCallback(() => {
    if (socket) socket.emit('typing');
  }, [socket]);

  const clearSubscriptionError = useCallback(() => {
      setSubscriptionRequiredData(null);
  }, []);

  return (
    <ChatContext.Provider value={{
      socket,
      matchStatus,
      onlineCount,
      partner,
      partnerId,
      partnerIsVip,
      partnerCustomNameColor,
      partnerAvatarUrl,
      messages,
      typing,
      roomId,
      subscriptionRequiredData,
      areFriends,
      findMatch,
      skipMatch,
      skipAndBlockMatch,
      stopSearch,
      sendMessage,
      sendMediaMessage,
      sendTyping,
      clearSubscriptionError
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
